import { MarkerEditorProperties, TextMarkerEditor } from "@markerjs/markerjs3"
import LSTextMarker from "./LSTextMarker";
import { wrapTextByWidth } from "./MarkerHelpers";

export default class LSTextMarkerEditor extends TextMarkerEditor {
    private _textWidthContext: CanvasRenderingContext2D;

    constructor(properties: MarkerEditorProperties<LSTextMarker>) {
        super(properties);

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        if (context) {
            this._textWidthContext = context;
        }

        const wrapText = ((this.marker.textBlock as any).wrapText as Function).bind(this.marker.textBlock);
        const newWrapText = () => {
            const textEditor = this.textBlockEditorContainer.children?.[0] as HTMLDivElement;

            if (textEditor) {
                const wrappedText = wrapTextByWidth(
                    this.marker.text,
                    this.marker.fontSize.value,
                    this.marker.fontFamily,
                    textEditor,
                    this._textWidthContext
                );

                return wrappedText;
            }

            return this.marker.text;
        };

        (this.marker.textBlock as any).wrapText = newWrapText;

        this.marker.textBlock.labelBackground.style.background = "#ff0000";
        this.marker.textBlock.labelBackground.setAttribute("fill", "#ff0000");
        this.marker.textBlock.wordWrap = true;

        /*
            The default behavior of markerjs V3 is weird for the content editable div. 
            The event listeners they have set up cause some weird behavior on mobile as well as pasting issues
            on mobile and desktop. Overriding the div element with mostly the same properties/styling as in 
            the source code but removing some of the event listeners they have attached.
            Reference: https://github.com/ailon/markerjs3/blob/eb088a37c7d206e9548c4b1bc0196856d5bd10b9/src/editor/TextBlockEditor.ts#L174 
        */


        const editor = (this.textBlockEditor as any).textEditor as HTMLDivElement;
        const clone = editor.cloneNode(false) as HTMLDivElement;

        clone.id = "new-text";
        clone.tabIndex = 0;
        clone.style.maxWidth = "300px";
        clone.style.wordBreak = "break-word";
        clone.style.whiteSpace = "pre-wrap";
        clone.style.borderRadius = "8px";
        clone.style.pointerEvents = 'auto';
        clone.style.display = 'flex';
        clone.style.flexDirection = 'column';
        clone.style.alignItems = 'center';
        clone.style.justifyContent = 'center';
        clone.style.width = `${this.textBlockEditor.width}px`;
        clone.style.height = `${this.textBlockEditor.height}px`;
        clone.style.overflow = 'hidden';
        clone.style.textAlign = 'center';
        clone.style.fontFamily = this.fontFamily;
        clone.style.fontSize = `${this.marker.fontSize?.value}${this.marker.fontSize?.units}`;
        clone.style.lineHeight = '1em';

        if (this.marker.text !== '') {
            clone.innerText = this.marker.text
        } else {
            clone.innerHTML = '&nbsp;';
        }

        clone.contentEditable = 'true';
        clone.style.outline = 'none';
        clone.style.color = this.marker.st;

        clone.addEventListener('keydown', (ev) => {
            if (ev.key === 'Escape') {
                ev.preventDefault();
                clone.blur();
            }
        });

        clone.addEventListener('keyup', (ev) => {
            ev.stopPropagation();
            this.textBlockEditor.text = clone.innerText;
            if (this.textBlockEditor.onTextChanged !== undefined) {
                this.textBlockEditor.onTextChanged(clone.innerText);
            }
        });

        clone.addEventListener('blur', () => {
            this.textBlockEditor.text = clone.innerText;
            if (this.textBlockEditor.onTextChanged !== undefined) {
                this.textBlockEditor.onTextChanged(clone.innerText);
            }
            if (this.textBlockEditor.onBlur !== undefined) {
                this.textBlockEditor.onBlur();
            }
        });

        // Try to focus and select the text in the contenteditable div on the next frame. This is best effort, doesn't work on iOS/Safari
        const focus = () => {
            const range = document.createRange();
            range.selectNodeContents(clone);

            const selection = window.getSelection();

            clone.focus();

            if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
            }
        };

        requestAnimationFrame(focus);

        (this.textBlockEditor as any).textEditor = clone;
        (this.textBlockEditor as any).isSetupCompleted = true;
    }

    deselect(): void {
        if (this.marker.text === "\n") {
            this.marker.text = "Text";

            const textEditor = this.textBlockEditorContainer.children?.[0] as HTMLDivElement;

            if (textEditor) {
                textEditor.innerHTML = "Text";
            }
        }

        super.deselect();
    }
}